import { Box, SxProps } from '@mui/material';
import { Theme, ThemeOptions } from '@mui/system';
import React, { useEffect, useRef } from 'react';
import { FormProvider } from '../../../form/FormContext';
import { useShallowMergeSxProps } from '../../../hooks/useShallowMergeSxProps';
import { SHARE_SETTINGS_MODE } from '../../constants';
import type { ShareSettingsScopeId, ShareSettingsViewModel } from '../../types';
import { useInternalShareSettings } from '../hooks/useInternalShareSettings';
import ShareActorList from './dataDisplay/ShareActorList';
import ShareAccessLevelFormField from './dataInput/ShareAccessLevelFormField';
import ShareActorSelectionFormField from './dataInput/ShareActorSelectionFormField';
import ShareDefaultActionFormField from './dataInput/ShareDefaultActionFormField';
import ShareDepartmentSelectionFormField from './dataInput/ShareDepartmentSelectionFormField';
import ShareNotificationFormField from './dataInput/ShareNotificationFormField';
import { ShareSettingsScopedContext } from './ShareSettingsScopedContext';

const CONTAINER_SX: SxProps<Theme> = { width: '100%' };

const ACCESS_LEVEL_SX: SxProps<Theme> = {
    display: 'flex',
    gap: 1,
    alignItems: 'flex-start',
    flexDirection: { xs: 'column', md: 'row' },
    width: '100%',
    justifyContent: 'space-between'
};

const DEFAULT_PERMISSION_SECTION_SX: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: { xs: 'flex-start', md: 'flex-end' },
    flex: 1
};
const RESTRICTED_ACTORS_AND_ACTION_SX: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1, mt: 2 };

export type CreateShareSettingsProps = {
    sx?: SxProps<ThemeOptions>;
    mode: typeof SHARE_SETTINGS_MODE.create;
    scopeId: ShareSettingsScopeId;
    actorType?: 'person' | 'individual-account';
    allowAddingShareActors?: boolean;
};

export type UpdateShareSettingsProps = {
    sx?: SxProps<ThemeOptions>;
    mode: typeof SHARE_SETTINGS_MODE.update;
    scopeId: ShareSettingsScopeId;
    initialShareSettings: ShareSettingsViewModel;
    actorType?: 'person' | 'individual-account';
    allowAddingShareActors?: boolean;
};

export type ShareSettingsProps = CreateShareSettingsProps | UpdateShareSettingsProps;

export default function ShareSettings(props: ShareSettingsProps) {
    const { sx, scopeId, mode, allowAddingShareActors = false } = props;

    const maybeInitialShareSettings = 'initialShareSettings' in props ? props.initialShareSettings : undefined;

    const { shareSettings, initializeShareSettingsScope, shareSettingsFormState, setShareSettingsFormState } =
        useInternalShareSettings(scopeId);

    const isRestricted = shareSettings.accessLevel === 'restricted';
    const isOpen = shareSettings.accessLevel === 'open';

    const isMounted = useRef(false);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;

            if (mode === SHARE_SETTINGS_MODE.update) {
                initializeShareSettingsScope({
                    mode: SHARE_SETTINGS_MODE.update,
                    initialShareSettings: maybeInitialShareSettings
                });
            } else {
                initializeShareSettingsScope({
                    mode: SHARE_SETTINGS_MODE.create
                });
            }
        }
    }, [initializeShareSettingsScope, maybeInitialShareSettings, mode]);

    const containerSx = useShallowMergeSxProps(CONTAINER_SX, sx);

    return (
        <ShareSettingsScopedContext value={scopeId}>
            <FormProvider state={shareSettingsFormState} setState={setShareSettingsFormState}>
                <Box sx={containerSx}>
                    <Box sx={ACCESS_LEVEL_SX}>
                        <ShareAccessLevelFormField />
                        {isRestricted && (
                            <Box sx={DEFAULT_PERMISSION_SECTION_SX}>
                                <ShareDefaultActionFormField />
                            </Box>
                        )}
                    </Box>

                    {isOpen && <ShareNotificationFormField allowAddingNotifyContactsOptions={allowAddingShareActors} />}

                    {isRestricted && (
                        <Box sx={RESTRICTED_ACTORS_AND_ACTION_SX}>
                            <ShareActorSelectionFormField allowAddingShareActors={allowAddingShareActors} />
                            <ShareDepartmentSelectionFormField />
                            <ShareNotificationFormField hideNotifyContactsSelection />
                            <ShareActorList />
                        </Box>
                    )}
                </Box>
            </FormProvider>
        </ShareSettingsScopedContext>
    );
}
